import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, untracked } from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { NurturingStore } from '@libs/nurturing';
import { NurturingFeature } from '@libs/store';
import { AssistantChatWindowService } from '../../assistant-chat-window.service';
import { AssistantLauncherService } from '../../assistant-launcher.service';
import { AssistantMessagesService } from '../../assistant-messages.service';
import { AssistantMessagePreviewComponent } from '../assistant-message-preview';
import { AssistantTrialBadgeComponent } from '../assistant-trial-badge';

@Component({
  selector: 'lib-assistant-launcher-button',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, AssistantMessagePreviewComponent, AssistantTrialBadgeComponent],
  templateUrl: './assistant-launcher-button.component.html',
  styleUrl: './assistant-launcher-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantLauncherButtonComponent {
  public readonly siteId = input<string | undefined>();
  public readonly isInsideTrialPeriod = input<boolean>(false);
  public readonly toggleClicked = output<void>();

  protected readonly assistantLauncherService = inject(AssistantLauncherService);
  private readonly assistantMessagesService = inject(AssistantMessagesService);
  private readonly assistantChatWindowService = inject(AssistantChatWindowService);
  private readonly nurturingStore = inject(NurturingStore);
  protected readonly unreadCount = this.assistantMessagesService.unreadCount;
  protected readonly latestPreview = this.assistantLauncherService.latestPreview;
  protected readonly nurturingFeature = NurturingFeature.PersonalAssistantNudge;
  protected readonly isChatWindowOpen = this.assistantChatWindowService.isOpen;
  // Identifies this button's standing request in the nurturing queue. The first-time experience has
  // no popover, so the request exists only to take the feature's turn in the queue.
  private readonly requestId = Symbol('assistant-first-time-request');

  constructor() {
    // The button is visible as soon as it mounts - preload messages here rather than waiting for
    // the chat window to open, so the window has data ready immediately.
    this.assistantMessagesService.loadIfNeeded();

    // First-time experience: the assistant has no nudge popover. It joins the queue while the feature is
    // visible and the window is closed, so it waits behind the guided tour like the nudge did.
    effect(() => {
      const isVisible = this.nurturingStore.visibleNurturingFeatures().includes(this.nurturingFeature);

      if (isVisible && !this.isChatWindowOpen()) {
        this.nurturingStore.requestShow(this.requestId, this.nurturingFeature);
      } else {
        this.nurturingStore.withdraw(this.requestId);
      }
    });
    inject(DestroyRef).onDestroy(() => this.nurturingStore.withdraw(this.requestId));

    // When the feature gets its turn in the queue, open the window.
    // The visibility check guards against a stale election reopening the window right after it's closed.
    effect(() => {
      const isNudgeElected = this.nurturingStore.activeNurturingFeature() === this.nurturingFeature;
      const isNudgeVisible = this.nurturingStore.visibleNurturingFeatures().includes(this.nurturingFeature);

      if (isNudgeElected && isNudgeVisible && !this.isChatWindowOpen()) {
        untracked(() => this.assistantChatWindowService.open());
      }
    });
  }

  protected onButtonClick(): void {
    this.assistantLauncherService.dismissNudge();
    this.toggleClicked.emit();
  }

  // Dismissing the preview only hides the nudge - it deliberately leaves the message unread, so the
  // launcher's unread counter stays until the user actually opens the chat.
  protected onPreviewCloseClick(): void {
    this.assistantLauncherService.hidePreview();
  }
}
